import { writeToClipboard } from "@src/appV2/NativeAdapters/clipboard.adapter";
import { Platform } from "@src/mobile/utils/platform";

/**
 * Copies text to clipboard using the Capacitor plugin on native platforms,
 * falling back to the browser Clipboard API on web.
 *
 * The browser Clipboard API (`navigator.clipboard`) is unavailable in
 * non-secure contexts and in embedded browsers/iframes that don't grant the
 * clipboard-write permission (e.g. embedded webviews, some preview
 * environments). In those cases we fall back to the legacy
 * `document.execCommand` path so the call never throws.
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (Platform.isMobile) {
    await writeToClipboard(text);
    return;
  }
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to legacy fallback when the Clipboard API rejects
      // (permission denied, non-secure context, embedded webview, etc.).
    }
  }
  copyViaExecCommand(text);
}

// Copy via the legacy `document.execCommand("copy")` path. Uses a contenteditable
// span + Selection API rather than a textarea + textarea.select(): textarea
// selection requires focus, which a focus-trapping container (e.g. an MUI
// Dialog) immediately steals back, leaving the textarea empty-selected and
// the copy command effectively clearing the clipboard.
function copyViaExecCommand(text: string): void {
  const span = document.createElement("span");
  span.textContent = text;
  span.style.whiteSpace = "pre";
  span.style.userSelect = "all";
  span.style.position = "fixed";
  span.style.opacity = "0";
  span.style.pointerEvents = "none";
  document.body.appendChild(span);

  const selection = window.getSelection();
  const previousRanges: Range[] = [];
  if (selection) {
    for (let index = 0; index < selection.rangeCount; index += 1) {
      previousRanges.push(selection.getRangeAt(index));
    }
    selection.removeAllRanges();
    const range = document.createRange();
    range.selectNodeContents(span);
    selection.addRange(range);
  }

  try {
    document.execCommand("copy");
  } finally {
    if (selection) {
      selection.removeAllRanges();
      previousRanges.forEach((range) => selection.addRange(range));
    }
    span.remove();
  }
}
